<template>
  <div v-if="isAccountAreaValue || isLayoutNeutralSection">
    <MappedComponents :items="items" />
  </div>
  <div
    v-else-if="section?.fields.fullWidth"
    :class="$style['full-width-section']"
  >
    <MappedComponents :items="items" />
  </div>
  <ContentSection
    v-else
    :id="resolvedSectionId"
    :class="classes"
    :color-mode="section?.fields.theme ?? 'light'"
  >
    <MappedComponents :items="items" />
  </ContentSection>
</template>

<script setup lang="ts">
import { ContentSection } from '@hypercodestudio/basler-components';
import type { Entry } from '~~/lib/Contentful/types';
import type { ComputedRef, Ref } from 'vue';
import { unref } from 'vue';
import type { ISection, ITechnicalComponent } from '~~/lib/ContentfulService';
import { IS_ACCOUNT_AREA } from '~~/lib/constants';
import { resolveContentSectionId } from '~/utils/resolveContentSectionId';
import { isTypeformLayoutNeutralConfiguration } from '~/utils/typeformSurvey';

interface Props {
  section: ISection | undefined;
  items: Entry<unknown>[];
}

const props = defineProps<Props>();

const isAccountAreaRef = inject<Ref<boolean> | ComputedRef<boolean>>(
  IS_ACCOUNT_AREA,
  ref(false)
);
const isAccountAreaValue = computed(() => unref(isAccountAreaRef));
const resolvedSectionId = computed(
  () =>
    resolveContentSectionId(
      props.section?.fields?.id,
      props.section?.sys?.id,
      props.items[0]?.sys.id
    ) ?? 'content-section'
);

function isLayoutNeutralItem(item: Entry<unknown>): boolean {
  if (item?.sys?.contentType?.sys?.id !== 'technicalComponent') {
    return false;
  }

  const technicalComponent = item as ITechnicalComponent;
  if (technicalComponent.fields?.type !== 'typeformSurvey') {
    return false;
  }

  return isTypeformLayoutNeutralConfiguration(
    technicalComponent.fields?.configuration
  );
}

/** Typeform overlay / unconfigured survey must not create section padding or white bg. */
const isLayoutNeutralSection = computed(
  () =>
    props.items.length > 0 &&
    props.items.every((item) => isLayoutNeutralItem(item))
);

const classes = computed(() => ({
  // XXX: there might be a more elegant solution to this.
  // @see https://gcp.baslerweb.com/jira/browse/DBP-930
  'content-section--color-transparent': props.items?.some((item) => {
    if (item?.sys?.contentType?.sys?.id === 'technicalComponent') {
      return (
        (item as ITechnicalComponent).fields?.type === 'documentCenter' ||
        (item as ITechnicalComponent).fields?.type === 'softwareCenter'
      );
    }

    return false;
  })
}));
</script>

<style module>
.full-width-section {
  background-color: #fff;
}
</style>
